import { useEffect, useState } from 'react';
import { objectToCamel } from 'ts-case-convert';
import { parseUnits } from 'ethers';

import { Agreement, getPrice, getPriceByProvider } from '@/shared/api/borrow';
import { useCentrifugeContext } from '@/app/providers/Centrifuge';

// USD code in hex according to ISO 4217
const USD_ADDRESS = '0x0000000000000000000000000000000000000348';

type EstimateRiskFactorResponse = {
    risk_factor: string;
    market_value: string;
    stress_tested_value: string;
};

export const useCalculateCollateralPrices = (
    collateralSum: string,
    agreementAddress: string,
    agreement: Agreement,
) => {
    const [prices, setPrices] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const { centrifuge } = useCentrifugeContext();
    const leverage = agreement?.leverage;

    const { address, symbol, decimals } = leverage || {};

    const estimateWithCentrifuge = async (): Promise<string | null> => {
        try {
            const params = {
                leverage: {
                    address,
                    amount: parseUnits('1', decimals).toString(),
                    symbol,
                    decimals,
                },
                collateral: [
                    {
                        address: USD_ADDRESS,
                        name: 'USD',
                        symbol: 'USD',
                        decimals: 18,
                        amount: parseUnits('1', 18).toString(),
                    },
                ],
                agreement_address: agreementAddress,
            };

            const response = await centrifuge?.rpc('estimate_risk_factor', params);
            const parsedData =
                typeof response?.data === 'string' ? JSON.parse(response.data) : response?.data;

            if (parsedData?.error) {
                console.error(parsedData.error);
                return null;
            }

            const data = objectToCamel(parsedData as { result: EstimateRiskFactorResponse });
            return Number(data?.result?.marketValue).toFixed(decimals);
        } catch (err) {
            console.error('Centrifuge estimation failed:', err);
            return null;
        }
    };

    const fetchLeverageToUSDPrice = async (): Promise<string | null> => {
        try {
            const collateralData = agreement.stressTestMatrix.find((a) => a.address === address);

            if (
                collateralData?.priceProvider &&
                collateralData?.priceModel &&
                collateralData.priceProvider !== 'N/A' &&
                collateralData.priceModel !== 'N/A'
            ) {
                const response = await getPriceByProvider(
                    address as string,
                    USD_ADDRESS,
                    collateralData.priceProvider,
                    collateralData.priceModel,
                );
                return response?.price || null;
            }

            const fallbackResponse = await getPrice(address as string, USD_ADDRESS);
            return fallbackResponse?.price || null;
        } catch (err: any) {
            if (err?.status === 404) {
                return await estimateWithCentrifuge();
            }
            console.error('Error fetching leverage to USD price:', err);
            return null;
        }
    };

    useEffect(() => {
        const fetchPrices = async () => {
            setLoading(true);
            setError(null);

            try {
                const priceStr = await fetchLeverageToUSDPrice();
                if (!priceStr) throw new Error('Price not available');

                const result = Number(collateralSum) * Number(priceStr);
                setPrices(result.toFixed(2));
            } catch (err) {
                setError((err as Error).message || 'Unexpected error');
            } finally {
                setLoading(false);
            }
        };

        fetchPrices();
    }, [collateralSum, agreementAddress, agreement]);

    return {
        prices,
        error,
        loading,
    };
};
